import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getTenants } from '../api/client';

export interface Site {
  id: number;
  name: string;
}

interface TenantData {
  id: number;
  name: string;
  sites: Site[];
}

// ---------------------------------------------------------------------------
// Context — single source of truth for tenants & sites
// ---------------------------------------------------------------------------

interface SiteContextValue {
  tenants: TenantData[];
  allSites: Site[];
  ready: boolean;
  reload: () => void;
}

const SiteContext = createContext<SiteContextValue>({
  tenants: [],
  allSites: [],
  ready: false,
  reload: () => {},
});

/** Wrap the authenticated part of the app so every child can read sites. */
export const SiteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tenants, setTenants] = useState<TenantData[]>([]);
  const [allSites, setAllSites] = useState<Site[]>([]);
  const [ready, setReady] = useState(false);

  const load = () => {
    getTenants()
      .then((res) => {
        const raw = res.data || res;
        const t: TenantData[] = [];
        const s: Site[] = [];
        for (const tenant of raw) {
          const sites: Site[] = [];
          for (const site of tenant.sites || []) {
            const mapped = { id: site.bookmaker_id, name: site.name };
            sites.push(mapped);
            s.push(mapped);
          }
          t.push({ id: tenant.id, name: tenant.name, sites });
        }
        setTenants(t);
        setAllSites(s);
        setReady(true);
      })
      .catch((err) => {
        console.error('Failed to load tenants/sites:', err);
        setReady(true); // avoid infinite loading state
      });
  };

  useEffect(() => { load(); }, []);

  return (
    <SiteContext.Provider value={{ tenants, allSites, ready, reload: load }}>
      {children}
    </SiteContext.Provider>
  );
};

/** Hook to access all sites data from context. */
export function useSites(): SiteContextValue {
  return useContext(SiteContext);
}

// ---------------------------------------------------------------------------
// Pure helper functions that accept the sites array from context
// ---------------------------------------------------------------------------

/** Look up a site name by id. Falls back to the numeric id as string. */
export function siteName(allSites: Site[], id: number | undefined | null): string {
  if (id == null) return '';
  const s = allSites.find((x) => x.id === id);
  return s?.name ?? String(id);
}

/** Convert selected IDs to a comma-separated query param string.
 *  When empty (= "All"), returns all site IDs. */
export function siteParam(allSites: Site[], selected: number[]): string {
  if (selected.length > 0) return selected.join(',');
  if (allSites.length > 0) return allSites.map((s) => s.id).join(',');
  return '';
}

/** Reset context on logout — call reload() from the context instead. */
export function clearSiteCache() {
  // no-op kept for backward compat; SiteProvider re-mounts on login anyway
}

// ---------------------------------------------------------------------------
// SiteFilter component — consumes context
// ---------------------------------------------------------------------------

interface SiteFilterProps {
  selected: number[];
  onChange: (ids: number[]) => void;
}

export const SiteFilter: React.FC<SiteFilterProps> = ({ selected, onChange }) => {
  const { t } = useTranslation();
  const { tenants } = useSites();
  const [selectedTenant, setSelectedTenant] = useState<number | null>(null);
  const [siteDropdownOpen, setSiteDropdownOpen] = useState(false);
  const siteRef = useRef<HTMLDivElement>(null);

  // Close site dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (siteRef.current && !siteRef.current.contains(e.target as Node)) setSiteDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Sites for the currently selected tenant (or all)
  const visibleSites = selectedTenant !== null
    ? tenants.find(t => t.id === selectedTenant)?.sites || []
    : tenants.flatMap(t => t.sites);

  const visibleIds = new Set(visibleSites.map(s => s.id));
  const allSelected = selected.length === 0 ||
    (selected.length === visibleSites.length && selected.every(id => visibleIds.has(id)));

  const handleTenantChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    if (val === 'all') {
      setSelectedTenant(null);
      onChange([]); // all sites
    } else {
      const tid = parseInt(val, 10);
      setSelectedTenant(tid);
      const tenantSites = tenants.find(t => t.id === tid)?.sites || [];
      onChange(tenantSites.map(s => s.id));
    }
  };

  const toggleAll = () => {
    if (allSelected) {
      onChange([]);
    } else {
      onChange(visibleSites.map(s => s.id));
    }
  };

  const toggle = (id: number) => {
    if (selected.includes(id)) {
      onChange(selected.filter(s => s !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  const siteLabel = allSelected
    ? t('siteFilter.allSites')
    : selected.length === 1
      ? visibleSites.find(s => s.id === selected[0])?.name ?? `1 ${t('siteFilter.selected')}`
      : `${selected.length} ${t('siteFilter.sites')}`;

  return (
    <div className="flex items-end gap-3">
      {/* Tenant select */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {t('siteFilter.tenantLabel')}
        </label>
        <select
          value={selectedTenant ?? 'all'}
          onChange={handleTenantChange}
          className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-700 dark:text-white min-w-[180px]"
        >
          <option value="all">{t('siteFilter.allTenants')}</option>
          {tenants.map(tenant => (
            <option key={tenant.id} value={tenant.id}>{tenant.name}</option>
          ))}
        </select>
      </div>

      {/* Site multi-select dropdown */}
      <div ref={siteRef} className="relative">
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {t('siteFilter.sitesLabel')}
        </label>
        <button
          type="button"
          onClick={() => setSiteDropdownOpen(!siteDropdownOpen)}
          className="flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-700 dark:text-white dark:hover:bg-slate-600 min-w-[200px] justify-between"
        >
          <span className="truncate">{siteLabel}</span>
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${siteDropdownOpen ? 'rotate-180' : ''}`} />
        </button>

        {siteDropdownOpen && (
          <div className="absolute z-30 mt-1 w-72 rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-600 dark:bg-slate-800">
            <label className="flex cursor-pointer items-center gap-3 border-b border-slate-200 px-4 py-2.5 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-700">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                className="h-4 w-4 rounded border-slate-300 text-accent focus:ring-accent dark:border-slate-500 dark:bg-slate-700"
              />
              <span className="text-sm font-medium text-slate-900 dark:text-white">{t('siteFilter.selectAll')}</span>
            </label>

            <div className="max-h-60 overflow-y-auto py-1">
              {visibleSites.map(s => {
                const checked = allSelected || selected.includes(s.id);
                return (
                  <label
                    key={s.id}
                    className="flex cursor-pointer items-center gap-3 px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-700"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(s.id)}
                      className="h-4 w-4 rounded border-slate-300 text-accent focus:ring-accent dark:border-slate-500 dark:bg-slate-700"
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-300">{s.name}</span>
                    <span className="ml-auto text-xs text-slate-400">ID {s.id}</span>
                  </label>
                );
              })}
              {visibleSites.length === 0 && (
                <p className="px-4 py-3 text-sm text-slate-400">{t('siteFilter.noSites')}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
